import { useState, useRef } from 'react';

// ─── Constants ────────────────────────────────────────────────────────────────

const BASE_URL = 'https://api.axetra.io/v1/customer';

type Lang = 'curl' | 'php' | 'python';

// ─── Helper components ────────────────────────────────────────────────────────

function MethodBadge({ method }: { method: 'GET' | 'POST' | 'PUT' | 'DELETE' }) {
  const colors: Record<string, string> = {
    GET: 'bg-blue-100 text-blue-800',
    POST: 'bg-green-100 text-green-800',
    PUT: 'bg-amber-100 text-amber-800',
    DELETE: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-bold font-mono ${colors[method]}`}>
      {method}
    </span>
  );
}

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code.trim());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <div className="relative group">
      <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-sm overflow-x-auto whitespace-pre">
        {code.trim()}
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-3 right-3 text-gray-500 hover:text-gray-200 transition-colors opacity-0 group-hover:opacity-100"
        title="Copy"
      >
        {copied ? (
          <svg className="h-4 w-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </button>
    </div>
  );
}

function ParamTable({ params }: { params: { name: string; type: string; required: boolean; description: string }[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 pr-4 font-medium text-gray-700 w-40">Parameter</th>
            <th className="text-left py-2 pr-4 font-medium text-gray-700 w-24">Type</th>
            <th className="text-left py-2 pr-4 font-medium text-gray-700 w-20">Required</th>
            <th className="text-left py-2 font-medium text-gray-700">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {params.map((p) => (
            <tr key={p.name}>
              <td className="py-2 pr-4 font-mono text-gray-900">{p.name}</td>
              <td className="py-2 pr-4 text-gray-500">{p.type}</td>
              <td className="py-2 pr-4">
                {p.required ? (
                  <span className="text-red-600 font-medium">Yes</span>
                ) : (
                  <span className="text-gray-400">No</span>
                )}
              </td>
              <td className="py-2 text-gray-600">{p.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LangSwitcher({ active, onChange }: { active: Lang; onChange: (l: Lang) => void }) {
  const langs: { value: Lang; label: string }[] = [
    { value: 'curl', label: 'cURL' },
    { value: 'php', label: 'PHP' },
    { value: 'python', label: 'Python' },
  ];
  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 mb-3">
      {langs.map((l) => (
        <button
          key={l.value}
          onClick={() => onChange(l.value)}
          className={`px-3 py-1 text-sm rounded-md font-medium transition-colors ${
            active === l.value
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

// ─── Section anchor wrapper ───────────────────────────────────────────────────

function Section({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-20">
      {children}
    </section>
  );
}

// ─── Endpoint block ───────────────────────────────────────────────────────────

interface EndpointProps {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  description: string;
  params?: { name: string; type: string; required: boolean; description: string }[];
  responseSample: string;
  lang: Lang;
  curlExample: string;
  phpExample: string;
  pythonExample: string;
}

function Endpoint({
  method,
  path,
  description,
  params,
  responseSample,
  lang,
  curlExample,
  phpExample,
  pythonExample,
}: EndpointProps) {
  const codeMap: Record<Lang, string> = { curl: curlExample, php: phpExample, python: pythonExample };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <MethodBadge method={method} />
        <code className="text-sm font-mono text-gray-800">{path}</code>
      </div>
      <div className="px-4 py-4 space-y-4">
        <p className="text-sm text-gray-600">{description}</p>

        {params && params.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Parameters</h4>
            <ParamTable params={params} />
          </div>
        )}

        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Example request</h4>
          <CodeBlock code={codeMap[lang]} />
        </div>

        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Example response</h4>
          <CodeBlock code={responseSample} />
        </div>
      </div>
    </div>
  );
}

// ─── Sidebar nav ──────────────────────────────────────────────────────────────

const SECTIONS = [
  { id: 'authentication', label: 'Authentication' },
  { id: 'campaigns', label: 'Campaigns' },
  { id: 'statistics', label: 'Statistics' },
  { id: 'team', label: 'Team' },
  { id: 'account', label: 'Account' },
];

function SidebarNav({ activeSection }: { activeSection: string }) {
  return (
    <nav className="space-y-1">
      {SECTIONS.map((s) => (
        <a
          key={s.id}
          href={`#${s.id}`}
          className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeSection === s.id
              ? 'bg-primary-50 text-primary-700'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          }`}
        >
          {s.label}
        </a>
      ))}
    </nav>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ApiDocsPage() {
  const [lang, setLang] = useState<Lang>('curl');
  const [activeSection, setActiveSection] = useState('authentication');
  const mainRef = useRef<HTMLDivElement>(null);

  // Track active section via IntersectionObserver would be ideal but
  // a simpler scroll handler works well for this use case
  const handleScroll = () => {
    const offsets = SECTIONS.map((s) => {
      const el = document.getElementById(s.id);
      if (!el) return { id: s.id, top: Infinity };
      return { id: s.id, top: el.getBoundingClientRect().top };
    });
    const visible = offsets.filter((o) => o.top <= 120);
    if (visible.length > 0) {
      setActiveSection(visible[visible.length - 1].id);
    }
  };

  return (
    <div className="flex gap-8 relative" onScroll={handleScroll}>
      {/* Left sticky sidebar */}
      <aside className="hidden lg:block w-48 flex-shrink-0">
        <div className="sticky top-20">
          <p className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Sections
          </p>
          <SidebarNav activeSection={activeSection} />
        </div>
      </aside>

      {/* Main content */}
      <div ref={mainRef} className="flex-1 min-w-0 space-y-10 pb-16">
        {/* Page header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Reference</h1>
          <p className="text-sm text-gray-500 mt-1">
            Full reference for the Axetra Customer API. Authenticate with an{' '}
            <a href="/settings/api-keys" className="text-primary-600 hover:underline">API key</a>.
          </p>
        </div>

        {/* Language switcher — global, controls all code blocks */}
        <div className="bg-white shadow rounded-lg px-6 py-4 flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700">Code examples in:</span>
          <LangSwitcher active={lang} onChange={setLang} />
        </div>

        {/* ── Authentication ── */}
        <Section id="authentication">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Authentication</h2>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Base URL</p>
                <code className="text-sm bg-gray-100 px-3 py-1.5 rounded font-mono text-gray-800">
                  {BASE_URL}
                </code>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-2">
                  All requests require a <span className="font-medium">Bearer token</span> in the Authorization header.
                  Generate an API key from the{' '}
                  <a href="/settings/api-keys" className="text-primary-600 hover:underline">API Keys</a>{' '}
                  settings page.
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 font-mono text-sm text-gray-800">
                  Authorization: Bearer axk_your_token_here
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Authentication example</h3>
                <LangSwitcher active={lang} onChange={setLang} />
                <CodeBlock
                  code={
                    lang === 'curl'
                      ? `curl -X GET "${BASE_URL}/campaigns" \\
  -H "Authorization: Bearer axk_your_token_here" \\
  -H "Accept: application/json"`
                      : lang === 'php'
                      ? `<?php
$ch = curl_init('${BASE_URL}/campaigns');
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'Authorization: Bearer axk_your_token_here',
    'Accept: application/json',
]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
curl_close($ch);
$data = json_decode($response, true);`
                      : `import requests

headers = {
    'Authorization': 'Bearer axk_your_token_here',
    'Accept': 'application/json',
}
response = requests.get('${BASE_URL}/campaigns', headers=headers)
data = response.json()`
                  }
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
                <svg className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-xs text-amber-800">
                  Keep your API key secret. Never expose it in client-side JavaScript or commit it to version control.
                </p>
              </div>
            </div>
          </div>
        </Section>

        {/* ── Campaigns ── */}
        <Section id="campaigns">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Campaigns</h2>
              <p className="text-sm text-gray-500 mt-0.5">Manage cloaking campaigns, filters, and A/B tests.</p>
            </div>
            <div className="px-6 py-5 space-y-2">

              <Endpoint
                method="GET"
                path="/campaigns"
                description="List all campaigns belonging to your account. Returns an array sorted by creation date (newest first)."
                lang={lang}
                responseSample={`{
  "data": [
    {
      "uuid": "cm_abc123",
      "name": "Facebook Summer Campaign",
      "status": "active",
      "created_at": "2025-06-01T12:00:00Z"
    }
  ],
  "meta": { "total": 1, "page": 1, "per_page": 25 }
}`}
                curlExample={`curl -X GET "${BASE_URL}/campaigns" \\
  -H "Authorization: Bearer axk_your_token_here"`}
                phpExample={`$response = $client->get('${BASE_URL}/campaigns');
$campaigns = json_decode($response->getBody(), true);`}
                pythonExample={`response = requests.get('${BASE_URL}/campaigns', headers=headers)
campaigns = response.json()`}
              />

              <Endpoint
                method="POST"
                path="/campaigns"
                description="Create a new cloaking campaign."
                params={[
                  { name: 'name', type: 'string', required: true, description: 'Human-readable campaign name' },
                  { name: 'offer_url', type: 'string', required: true, description: 'URL to show real visitors' },
                  { name: 'safe_url', type: 'string', required: true, description: 'URL to show bots/reviewers' },
                  { name: 'geo', type: 'string[]', required: false, description: 'Allowed country codes (ISO 3166-1 alpha-2)' },
                ]}
                lang={lang}
                responseSample={`{
  "uuid": "cm_xyz789",
  "name": "New Campaign",
  "api_key": "klo_...",
  "status": "active",
  "created_at": "2025-07-01T09:00:00Z"
}`}
                curlExample={`curl -X POST "${BASE_URL}/campaigns" \\
  -H "Authorization: Bearer axk_your_token_here" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"New Campaign","offer_url":"https://offer.example","safe_url":"https://safe.example"}'`}
                phpExample={`$response = $client->post('${BASE_URL}/campaigns', [
    'json' => [
        'name' => 'New Campaign',
        'offer_url' => 'https://offer.example',
        'safe_url' => 'https://safe.example',
    ],
]);`}
                pythonExample={`payload = {
    'name': 'New Campaign',
    'offer_url': 'https://offer.example',
    'safe_url': 'https://safe.example',
}
response = requests.post('${BASE_URL}/campaigns', json=payload, headers=headers)`}
              />

              <Endpoint
                method="GET"
                path="/campaigns/{uuid}"
                description="Get full details for a single campaign, including its current filter config."
                params={[
                  { name: 'uuid', type: 'string', required: true, description: 'Campaign UUID from the list endpoint' },
                ]}
                lang={lang}
                responseSample={`{
  "uuid": "cm_abc123",
  "name": "Facebook Summer Campaign",
  "offer_url": "https://offer.example",
  "safe_url": "https://safe.example",
  "status": "active",
  "filter": { "block_vpn": true, "block_proxy": true },
  "created_at": "2025-06-01T12:00:00Z"
}`}
                curlExample={`curl -X GET "${BASE_URL}/campaigns/cm_abc123" \\
  -H "Authorization: Bearer axk_your_token_here"`}
                phpExample={`$response = $client->get("${BASE_URL}/campaigns/cm_abc123");
$campaign = json_decode($response->getBody(), true);`}
                pythonExample={`response = requests.get('${BASE_URL}/campaigns/cm_abc123', headers=headers)
campaign = response.json()`}
              />

              <Endpoint
                method="PUT"
                path="/campaigns/{uuid}"
                description="Update campaign properties such as name, offer URL, or safe URL."
                params={[
                  { name: 'name', type: 'string', required: false, description: 'New campaign name' },
                  { name: 'offer_url', type: 'string', required: false, description: 'Updated offer URL' },
                  { name: 'safe_url', type: 'string', required: false, description: 'Updated safe URL' },
                ]}
                lang={lang}
                responseSample={`{ "uuid": "cm_abc123", "name": "Updated Name", "status": "active" }`}
                curlExample={`curl -X PUT "${BASE_URL}/campaigns/cm_abc123" \\
  -H "Authorization: Bearer axk_your_token_here" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"Updated Name"}'`}
                phpExample={`$response = $client->put("${BASE_URL}/campaigns/cm_abc123", [
    'json' => ['name' => 'Updated Name'],
]);`}
                pythonExample={`response = requests.put(
    '${BASE_URL}/campaigns/cm_abc123',
    json={'name': 'Updated Name'},
    headers=headers,
)`}
              />

              <Endpoint
                method="DELETE"
                path="/campaigns/{uuid}"
                description="Permanently delete a campaign and all associated request logs. This action is irreversible."
                lang={lang}
                responseSample={`{ "message": "Campaign deleted successfully" }`}
                curlExample={`curl -X DELETE "${BASE_URL}/campaigns/cm_abc123" \\
  -H "Authorization: Bearer axk_your_token_here"`}
                phpExample={`$response = $client->delete("${BASE_URL}/campaigns/cm_abc123");`}
                pythonExample={`response = requests.delete('${BASE_URL}/campaigns/cm_abc123', headers=headers)`}
              />

              <Endpoint
                method="PUT"
                path="/campaigns/{uuid}/filter"
                description="Replace the bot-detection filter configuration for a campaign."
                params={[
                  { name: 'block_vpn', type: 'boolean', required: false, description: 'Block VPN traffic' },
                  { name: 'block_proxy', type: 'boolean', required: false, description: 'Block proxy traffic' },
                  { name: 'blocked_countries', type: 'string[]', required: false, description: 'ISO 3166-1 alpha-2 country codes to block' },
                  { name: 'blocked_ips', type: 'string[]', required: false, description: 'IP addresses or CIDR ranges to block' },
                ]}
                lang={lang}
                responseSample={`{ "filter": { "block_vpn": true, "block_proxy": false, "blocked_countries": ["CN"] } }`}
                curlExample={`curl -X PUT "${BASE_URL}/campaigns/cm_abc123/filter" \\
  -H "Authorization: Bearer axk_your_token_here" \\
  -H "Content-Type: application/json" \\
  -d '{"block_vpn":true,"blocked_countries":["CN"]}'`}
                phpExample={`$response = $client->put("${BASE_URL}/campaigns/cm_abc123/filter", [
    'json' => ['block_vpn' => true, 'blocked_countries' => ['CN']],
]);`}
                pythonExample={`response = requests.put(
    '${BASE_URL}/campaigns/cm_abc123/filter',
    json={'block_vpn': True, 'blocked_countries': ['CN']},
    headers=headers,
)`}
              />

              <Endpoint
                method="PUT"
                path="/campaigns/{uuid}/ab-test"
                description="Configure A/B test split for a campaign. Traffic is distributed between offer URLs according to specified weights."
                params={[
                  { name: 'enabled', type: 'boolean', required: true, description: 'Enable or disable A/B testing' },
                  { name: 'variants', type: 'object[]', required: true, description: 'Array of {url, weight} objects (weights must sum to 100)' },
                ]}
                lang={lang}
                responseSample={`{
  "ab_test": {
    "enabled": true,
    "variants": [
      { "url": "https://offer-a.example", "weight": 60 },
      { "url": "https://offer-b.example", "weight": 40 }
    ]
  }
}`}
                curlExample={`curl -X PUT "${BASE_URL}/campaigns/cm_abc123/ab-test" \\
  -H "Authorization: Bearer axk_your_token_here" \\
  -H "Content-Type: application/json" \\
  -d '{"enabled":true,"variants":[{"url":"https://offer-a.example","weight":60},{"url":"https://offer-b.example","weight":40}]}'`}
                phpExample={`$response = $client->put("${BASE_URL}/campaigns/cm_abc123/ab-test", [
    'json' => [
        'enabled' => true,
        'variants' => [
            ['url' => 'https://offer-a.example', 'weight' => 60],
            ['url' => 'https://offer-b.example', 'weight' => 40],
        ],
    ],
]);`}
                pythonExample={`response = requests.put(
    '${BASE_URL}/campaigns/cm_abc123/ab-test',
    json={
        'enabled': True,
        'variants': [
            {'url': 'https://offer-a.example', 'weight': 60},
            {'url': 'https://offer-b.example', 'weight': 40},
        ],
    },
    headers=headers,
)`}
              />

              <Endpoint
                method="POST"
                path="/campaigns/{uuid}/pause"
                description="Pause a campaign. While paused, the integration script will redirect all traffic to the safe URL."
                lang={lang}
                responseSample={`{ "uuid": "cm_abc123", "status": "paused" }`}
                curlExample={`curl -X POST "${BASE_URL}/campaigns/cm_abc123/pause" \\
  -H "Authorization: Bearer axk_your_token_here"`}
                phpExample={`$response = $client->post("${BASE_URL}/campaigns/cm_abc123/pause");`}
                pythonExample={`response = requests.post('${BASE_URL}/campaigns/cm_abc123/pause', headers=headers)`}
              />

              <Endpoint
                method="POST"
                path="/campaigns/{uuid}/activate"
                description="Re-activate a paused campaign. Bot detection resumes immediately."
                lang={lang}
                responseSample={`{ "uuid": "cm_abc123", "status": "active" }`}
                curlExample={`curl -X POST "${BASE_URL}/campaigns/cm_abc123/activate" \\
  -H "Authorization: Bearer axk_your_token_here"`}
                phpExample={`$response = $client->post("${BASE_URL}/campaigns/cm_abc123/activate");`}
                pythonExample={`response = requests.post('${BASE_URL}/campaigns/cm_abc123/activate', headers=headers)`}
              />

            </div>
          </div>
        </Section>

        {/* ── Statistics ── */}
        <Section id="statistics">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Statistics</h2>
              <p className="text-sm text-gray-500 mt-0.5">Query traffic and bot-detection statistics.</p>
            </div>
            <div className="px-6 py-5 space-y-2">

              <Endpoint
                method="GET"
                path="/statistics"
                description="Get account-wide traffic statistics aggregated by day for a date range."
                params={[
                  { name: 'start_date', type: 'string', required: true, description: 'Start date in YYYY-MM-DD format' },
                  { name: 'end_date', type: 'string', required: true, description: 'End date in YYYY-MM-DD format' },
                ]}
                lang={lang}
                responseSample={`{
  "total_requests": 48200,
  "humans": 31540,
  "bots": 16660,
  "bot_rate": 34.6,
  "by_day": [
    { "date": "2025-07-01", "requests": 6800, "humans": 4420, "bots": 2380 }
  ]
}`}
                curlExample={`curl -X GET "${BASE_URL}/statistics?start_date=2025-07-01&end_date=2025-07-31" \\
  -H "Authorization: Bearer axk_your_token_here"`}
                phpExample={`$response = $client->get('${BASE_URL}/statistics', [
    'query' => ['start_date' => '2025-07-01', 'end_date' => '2025-07-31'],
]);`}
                pythonExample={`response = requests.get(
    '${BASE_URL}/statistics',
    params={'start_date': '2025-07-01', 'end_date': '2025-07-31'},
    headers=headers,
)`}
              />

              <Endpoint
                method="GET"
                path="/campaigns/{uuid}/statistics"
                description="Get per-campaign traffic statistics for a date range. Includes country and device breakdowns."
                params={[
                  { name: 'uuid', type: 'string', required: true, description: 'Campaign UUID' },
                  { name: 'start_date', type: 'string', required: true, description: 'Start date in YYYY-MM-DD format' },
                  { name: 'end_date', type: 'string', required: true, description: 'End date in YYYY-MM-DD format' },
                ]}
                lang={lang}
                responseSample={`{
  "campaign_uuid": "cm_abc123",
  "total_requests": 12500,
  "humans": 8400,
  "bots": 4100,
  "bot_rate": 32.8,
  "top_countries": [
    { "country": "US", "requests": 5200 },
    { "country": "DE", "requests": 1800 }
  ]
}`}
                curlExample={`curl -X GET "${BASE_URL}/campaigns/cm_abc123/statistics?start_date=2025-07-01&end_date=2025-07-31" \\
  -H "Authorization: Bearer axk_your_token_here"`}
                phpExample={`$response = $client->get("${BASE_URL}/campaigns/cm_abc123/statistics", [
    'query' => ['start_date' => '2025-07-01', 'end_date' => '2025-07-31'],
]);`}
                pythonExample={`response = requests.get(
    f'${BASE_URL}/campaigns/cm_abc123/statistics',
    params={'start_date': '2025-07-01', 'end_date': '2025-07-31'},
    headers=headers,
)`}
              />

            </div>
          </div>
        </Section>

        {/* ── Team ── */}
        <Section id="team">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Team</h2>
              <p className="text-sm text-gray-500 mt-0.5">Manage team members and send invitations.</p>
            </div>
            <div className="px-6 py-5 space-y-2">

              <Endpoint
                method="GET"
                path="/team"
                description="List all members of your account, including their role and assigned campaigns."
                lang={lang}
                responseSample={`{
  "members": [
    {
      "id": 12,
      "name": "Alice Smith",
      "email": "alice@example.com",
      "role": "manager",
      "joined_at": "2025-03-15T10:00:00Z"
    }
  ]
}`}
                curlExample={`curl -X GET "${BASE_URL}/team" \\
  -H "Authorization: Bearer axk_your_token_here"`}
                phpExample={`$response = $client->get('${BASE_URL}/team');
$members = json_decode($response->getBody(), true)['members'];`}
                pythonExample={`response = requests.get('${BASE_URL}/team', headers=headers)
members = response.json()['members']`}
              />

              <Endpoint
                method="POST"
                path="/team/invite"
                description="Send an email invitation to a new team member. They will receive a link to accept and set up their account."
                params={[
                  { name: 'email', type: 'string', required: true, description: 'Email address of the person to invite' },
                  { name: 'role', type: 'string', required: true, description: '"manager" — the only supported role via API' },
                ]}
                lang={lang}
                responseSample={`{ "message": "Invitation sent to alice@example.com" }`}
                curlExample={`curl -X POST "${BASE_URL}/team/invite" \\
  -H "Authorization: Bearer axk_your_token_here" \\
  -H "Content-Type: application/json" \\
  -d '{"email":"alice@example.com","role":"manager"}'`}
                phpExample={`$response = $client->post('${BASE_URL}/team/invite', [
    'json' => ['email' => 'alice@example.com', 'role' => 'manager'],
]);`}
                pythonExample={`response = requests.post(
    '${BASE_URL}/team/invite',
    json={'email': 'alice@example.com', 'role': 'manager'},
    headers=headers,
)`}
              />

              <Endpoint
                method="DELETE"
                path="/team/{user_id}"
                description="Remove a team member from your account. Their access is revoked immediately."
                params={[
                  { name: 'user_id', type: 'integer', required: true, description: 'ID of the team member to remove' },
                ]}
                lang={lang}
                responseSample={`{ "message": "Team member removed successfully" }`}
                curlExample={`curl -X DELETE "${BASE_URL}/team/12" \\
  -H "Authorization: Bearer axk_your_token_here"`}
                phpExample={`$response = $client->delete("${BASE_URL}/team/12");`}
                pythonExample={`response = requests.delete('${BASE_URL}/team/12', headers=headers)`}
              />

            </div>
          </div>
        </Section>

        {/* ── Account ── */}
        <Section id="account">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Account</h2>
              <p className="text-sm text-gray-500 mt-0.5">Retrieve account and subscription information.</p>
            </div>
            <div className="px-6 py-5 space-y-2">

              <Endpoint
                method="GET"
                path="/account"
                description="Get details about the current account, including subscription plan limits and current usage."
                lang={lang}
                responseSample={`{
  "id": 42,
  "name": "Acme Ads",
  "owner_email": "owner@acme.com",
  "subscription": {
    "plan": "Pro",
    "status": "active",
    "campaigns_limit": 50,
    "campaigns_used": 12,
    "requests_limit": 500000,
    "requests_used": 88400,
    "renews_at": "2025-08-01"
  }
}`}
                curlExample={`curl -X GET "${BASE_URL}/account" \\
  -H "Authorization: Bearer axk_your_token_here"`}
                phpExample={`$response = $client->get('${BASE_URL}/account');
$account = json_decode($response->getBody(), true);`}
                pythonExample={`response = requests.get('${BASE_URL}/account', headers=headers)
account = response.json()`}
              />

            </div>
          </div>
        </Section>

        {/* Error codes reference */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Error codes</h2>
          </div>
          <div className="px-6 py-5">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 font-medium text-gray-700">Status</th>
                  <th className="text-left py-2 font-medium text-gray-700">Meaning</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { code: '200 OK', meaning: 'Request succeeded.' },
                  { code: '201 Created', meaning: 'Resource created successfully.' },
                  { code: '400 Bad Request', meaning: 'Invalid request body or parameters.' },
                  { code: '401 Unauthorized', meaning: 'Missing or invalid API key.' },
                  { code: '403 Forbidden', meaning: 'Your API key does not have the required scope.' },
                  { code: '404 Not Found', meaning: 'The requested resource does not exist.' },
                  { code: '422 Unprocessable Entity', meaning: 'Validation failed — check the errors field in the response.' },
                  { code: '429 Too Many Requests', meaning: 'Rate limit exceeded. Retry after the Retry-After header value.' },
                  { code: '500 Internal Server Error', meaning: 'Unexpected server error. Contact support if this persists.' },
                ].map((row) => (
                  <tr key={row.code}>
                    <td className="py-2 pr-4 font-mono text-gray-800">{row.code}</td>
                    <td className="py-2 text-gray-600">{row.meaning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
